import { useQuery, useMutation } from '@apollo/client';
import React, { useState, useMemo } from 'react';
import moment from 'moment-timezone';
import copy from 'copy-to-clipboard';
import { toast } from 'react-semantic-toasts';
import { ErrorHandler } from '@playbookux/playbook-web-core';
import {
  chain, filter, forEach, get, isEmpty, reduce,
} from 'lodash';
import {
  arrayOf, bool, func, number, shape, string,
} from 'prop-types';
import PlaybookTesterLabel from '../../../components/playbook-tester-label';

import { MARK_PROJECT_AS_DONE } from '../graphql';

import {
  GET_TEST_SESSIONS,
  handleInsertModeratedVideoMutationUpdate,
  handleMarkProjectAsDoneMutationUpdate,
  INSERT_MODERATED_VIDEO,
  LIMIT,
} from './graphql';

import {
  ActionsContainer,
  Button,
  Container,
  ContainerTesterName,
  Detail,
  Form,
  HeaderContainer,
  InsertError,
  Loader,
  OtherDetails,
  RemoveButton,
  Tester,
  TesterName,
  TesterEmail,
  TestDate,
  Link,
  ContainerSpace,
  ContainerParticipants,
} from './styles';

import {
  DEFAULT_TIMEZONE,
  MEETING_URL_NULL_ERROR_MESSAGE,
  MODALS,
} from './constants';

import { config } from '../../../../config';

import CapResponsesModal from '../cap-responses-modal';
import UploadVideo from '../../../../core/components/upload-video';
import ApprovalActionDropdown from '../../../../core/components/approval-action-dropdown';
// modals
import EditStatusModal from './edit-status-modal';
import RemoveModal from './remove-modal';
import { registerFunctions } from '../../../../core/hooks/use-function-handler';
import SegmentParticipant from '../segment-participant';

const TOAST_TIME = {
  success: 800,
  failure: 1200,
};

const ApprovalModeratedVideoLink = ({ project, refetch }) => {
  const [openModal, setOpenModal] = useState(null);
  const [selectedTestSession, setSelectedTestSession] = useState(null);

  const {
    id: projectId,
    description,
    audienceParticipants,
    segments,
    participantsSource: source,
    testType,
    createdBy: {
      profile: { firstName, lastName },
    },
  } = project;

  const {
    data = {},
    loading,
    error: queryError,
    refetch: refetchTestSessions,
  } = useQuery(GET_TEST_SESSIONS, {
    variables: {
      projectId,
      limit: LIMIT,
      removed: false,
      isModerated: testType === 'moderated',
    },
  });

  const [markProjectAsDone] = useMutation(MARK_PROJECT_AS_DONE, {
    update: (cache, mutation) => handleMarkProjectAsDoneMutationUpdate(cache, mutation, source),
    onCompleted: refetch,
  });

  const [insertModeratedVideo, { error: hasError }] = useMutation(
    INSERT_MODERATED_VIDEO,
    {
      update: handleInsertModeratedVideoMutationUpdate(projectId),
      onCompleted: refetch,
    },
  );

  const handleMarkProjectAsDone = () => markProjectAsDone({
    variables: {
      projectId,
    },
  });

  const handleMeetingCopy = (url) => {
    if (url) {
      copy(url);
      toast({
        description: 'Meeting link copied to the clipboard!',
        time: TOAST_TIME.success,
      });
    } else {
      toast({
        description: MEETING_URL_NULL_ERROR_MESSAGE,
        time: TOAST_TIME.failure,
      });
    }
  };

  const handleEmailCopy = (testerEmail) => {
    copy(testerEmail);
    toast({
      description: 'Tester email copied to the clipboard!',
      time: TOAST_TIME.success,
    });
  };

  const handleOpenModal = (testSession, modal) => {
    setSelectedTestSession(testSession);
    setOpenModal(modal);
  };

  const handleCloseModal = () => {
    setSelectedTestSession(null);
    setOpenModal(null);
  };

  const formatDate = (dateOriginal) => {
    if (isEmpty(dateOriginal)) return '';

    const dateTimezone = moment(dateOriginal).tz(DEFAULT_TIMEZONE);
    const date = dateTimezone.format('MM/DD/YYYY');
    const time = dateTimezone.format('hh:mma z');

    return `${date} at ${time}`;
  };

  const { testSessions = [] } = data;
  const testSessionsProcessed = useMemo(() => filter(testSessions, { status: 'processed' }), [testSessions]);

  // test sessions that do not count as a slot (expired) but are still being shown in the 'video link' list
  const testSessionsOverflown = useMemo(() => {
    let count = 0;
    forEach(testSessions, (testSession) => {
      if (testSession?.expired) {
        count += 1;
      }
    });
    return count;
  }, [testSessions]);

  const testSessionsWithSegment = useMemo(() => reduce(
    segments,
    (result, segment) => {
      const { id, name, audienceParticipants: participants } = segment;
      const testSessionsGroupedBySegment = filter(testSessions, (testSession) => testSession.tester.segment.id === id
        && testSession.bookingStatus !== 'rescheduled'
        && testSession.bookingStatus !== 'canceled');
      return {
        ...result,
        [name]: {
          participants,
          testSessions: testSessionsGroupedBySegment,
        },
      };
    },
    {},
  ), [segments, testSessions]);

  if (loading) {
    return (
      <Container>
        <Loader size="small" data-loader="approvals-moderated-video-loader" />
      </Container>
    );
  }

  const error = queryError;

  if (error) {
    return (
      <ErrorHandler
        error={error}
        defaultMessage="Error while retrieving the testers."
      />
    );
  }

  registerFunctions(`refetch_test_sessions_${projectId}`, {
    func1: refetchTestSessions,
  });

  return (
    <>
      <Container>
        <HeaderContainer>
          <OtherDetails>Other Details:</OtherDetails>
          <Detail>{`Description: ${description}`}</Detail>
          <Detail>{`Created by: ${firstName} ${lastName}`}</Detail>
        </HeaderContainer>

        <ContainerSpace>
          <div>
            {segments && segments.map((segment) => (
              <ContainerParticipants key={segment.id}>
                <Detail>
                  {`${segment.name} # Participants: ${segment.audienceParticipants}`}
                </Detail>
                <CapResponsesModal
                  label="Response Cap"
                  projectId={projectId}
                  number={segment.audienceParticipants}
                  segmentId={segment.id}
                  refetch={refetch}
                />
              </ContainerParticipants>
            ))}
          </div>
        </ContainerSpace>

        <ActionsContainer>
          {Object.keys(testSessionsWithSegment).map((key, index) => (
            <>
              <SegmentParticipant
                isModerated
                title={key}
                key={key}
                index={index}
                testSessionsWithSegment={testSessionsWithSegment[key]}
                testSessionsOverflown={testSessionsOverflown}
              />
              {testSessionsWithSegment[key].testSessions.length > 0 ? chain(testSessionsWithSegment[key].testSessions)
                .map(({
                  id: testSessionId, tester, removed, startAt, meetingUrlTester, bookingStatus,
                }) => {
                  const testerFirstName = get(tester, 'user.profile.firstName', '');
                  const testerLastName = get(tester, 'user.profile.lastName', '');
                  const testerEmail = get(tester, 'user.email', '');

                  const intermediateMeetingPageUrl = `
                    ${config.REACT_APP_TESTER_PLATFORM_URI}/moderated-meeting?test-session=${testSessionId}
                  `;

                  return (
                    <Tester key={tester.id}>
                      <ContainerTesterName>
                        <TesterName hasMeetingUrl={!!meetingUrlTester} onClick={() => handleMeetingCopy(intermediateMeetingPageUrl)}>
                          {`${testerFirstName} ${testerLastName}`}
                        </TesterName>
                        <TesterEmail onClick={() => handleEmailCopy(testerEmail)}>
                          {testerEmail}
                        </TesterEmail>
                      </ContainerTesterName>

                      <TestDate>
                        {formatDate(startAt)}
                      </TestDate>

                      <Form>
                        <Form.Group>
                          <Form.Field>
                            <UploadVideo
                              sessionId={testSessionId}
                              onUploadCompleted={insertModeratedVideo}
                            />
                          </Form.Field>
                          {hasError && (
                            <InsertError>Error while inserting video</InsertError>
                          )}
                        </Form.Group>
                      </Form>

                      {!removed && (
                        <RemoveButton
                          onClick={() => handleOpenModal({
                            id: testSessionId,
                            firstName: testerFirstName,
                            lastName: testerLastName,
                          }, MODALS.remove)}
                        >
                          Remove
                        </RemoveButton>
                      )}

                      <ApprovalActionDropdown
                        tester={tester}
                        projectId={project.id}
                      />

                      <Link onClick={() => handleOpenModal({
                        id: testSessionId,
                        firstName: testerFirstName,
                        lastName: testerLastName,
                        bookingStatus,
                      }, MODALS.editStatus)}
                      >
                        Edit Status
                      </Link>
                      {tester.user.tester && !tester.user.guest && project.participantsSource === 'playbook' && (
                        <PlaybookTesterLabel />
                      )}
                    </Tester>
                  );
                })
                .value() : <Tester>No sessions</Tester>}
            </>
          ))}

          {selectedTestSession && (
            <>
              <EditStatusModal
                open={openModal === MODALS.editStatus}
                testSession={selectedTestSession}
                projectId={projectId}
                handleCloseModal={handleCloseModal}
                refetch={refetch}
              />

              <RemoveModal
                open={openModal === MODALS.remove}
                handleCloseModal={handleCloseModal}
                projectId={projectId}
                testSession={selectedTestSession}
                refetchTestSessions={refetchTestSessions}
                refetchLoading={loading}
                refetch={refetch}
              />
            </>
          )}

          {testSessionsProcessed.length >= audienceParticipants && (
            <Button onClick={handleMarkProjectAsDone}>Mark as Done</Button>
          )}
        </ActionsContainer>
      </Container>
    </>
  );
};

ApprovalModeratedVideoLink.propTypes = {
  project: shape({
    audienceParticipants: number,
    participantsSource: string,
    createdBy: shape({
      profile: shape({
        lastName: string,
        firstName: string,
      }),
    }),
    segments: arrayOf(shape()),
    description: string,
    id: string,
    submittedAvailabilities: bool,
    testType: string,
  }).isRequired,
  refetch: func.isRequired,
};

export default ApprovalModeratedVideoLink;
